import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-textBase">Privacy Policy</h1>
              <p className="text-grey-600">Last updated: {currentDate}</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-8 space-y-8"
        >
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Introduction</h2>
            <p className="text-grey-600 leading-relaxed">
              FinInteract ("we," "our," or "us") operates the Financial Goal Tracker mobile application. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our app. By using Financial Goal Tracker, you agree to the collection and use 
              of information in accordance with this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4 flex items-center">
              <Database className="w-6 h-6 mr-2 text-primary" />
              Information We Collect
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-textBase mb-2">Personal Information</h3>
                <ul className="list-disc list-inside text-grey-600 space-y-1 ml-4">
                  <li>Name and email address (for account creation)</li>
                  <li>Profile information and preferences</li>
                  <li>Device information and app usage data</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-textBase mb-2">Financial Data</h3>
                <ul className="list-disc list-inside text-grey-600 space-y-1 ml-4">
                  <li>Financial goals and targets you set</li>
                  <li>Transaction data you input</li>
                  <li>Spending patterns and categories</li>
                  <li>Progress tracking information</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-textBase mb-2">Technical Information</h3>
                <ul className="list-disc list-inside text-grey-600 space-y-1 ml-4">
                  <li>Device type and operating system</li>
                  <li>App version and usage statistics</li>
                  <li>IP address and location data (anonymized)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">How We Use Your Information</h2>
            <ul className="list-disc list-inside text-grey-600 space-y-2 ml-4">
              <li>To provide and maintain the Financial Goal Tracker service</li>
              <li>To process and store your financial goals and transactions</li>
              <li>To generate insights and progress reports</li>
              <li>To improve our app functionality and user experience</li>
              <li>To send important updates and notifications</li>
              <li>To provide customer support and respond to inquiries</li>
              <li>To ensure app security and prevent fraud</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-primary" />
              Data Security
            </h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-grey-600 leading-relaxed mb-4">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside text-grey-600 space-y-2 ml-4">
                <li><strong>End-to-end encryption</strong> for sensitive financial data</li>
                <li><strong>Secure cloud storage</strong> with multiple layers of protection</li>
                <li><strong>Regular security audits</strong> and vulnerability assessments</li>
                <li><strong>Access controls</strong> and authentication measures</li>
                <li><strong>Data backup</strong> and disaster recovery procedures</li>
              </ul>
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>Important:</strong> While we implement comprehensive security measures, 
                  no method of transmission over the internet or electronic storage is 100% secure. 
                  We cannot guarantee absolute security of your data.
                </p>
              </div>
            </div>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Data Sharing and Disclosure</h2>
            <p className="text-grey-600 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-grey-600 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our app</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4 flex items-center">
              <Eye className="w-6 h-6 mr-2 text-primary" />
              Your Rights
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-textBase mb-2">Access Your Data</h3>
                <p className="text-sm text-grey-600">Request a copy of all personal data we hold about you</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-textBase mb-2">Update Information</h3>
                <p className="text-sm text-grey-600">Correct or update your personal information</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-textBase mb-2">Delete Account</h3>
                <p className="text-sm text-grey-600">Request deletion of your account and associated data</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-textBase mb-2">Data Portability</h3>
                <p className="text-sm text-grey-600">Export your data in a machine-readable format</p>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Data Retention</h2>
            <p className="text-grey-600 leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to provide services. 
              If you delete your account, we will delete or anonymize your personal information within 30 days, 
              except where we are required to retain certain information for legal or regulatory purposes.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Children's Privacy</h2>
            <p className="text-grey-600 leading-relaxed">
              Financial Goal Tracker is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you are a parent or guardian and believe your child 
              has provided us with personal information, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Changes to This Privacy Policy</h2>
            <p className="text-grey-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
              the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to 
              review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Contact Us</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-grey-600 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-grey-600">
                <p><strong>Email:</strong> privacy@fininteract.com</p>
                <p><strong>Location:</strong> Remote</p>
              </div>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
